import { useCallback, useContext, useEffect, useMemo } from "react";

import { createError, parseGraphQLError } from "utils";

import { useQuery } from "hooks";
import { createResolverFactory } from "components/QueryClient/ResolverFactory";

import { ToasterContext } from "scenes/Hubly/components/Workspace/Provider";

function usePlanFeature(featureName, options = {}) {
  const { fireError } = useContext(ToasterContext);
  const { showToasterOnError = true } = options;

  const queryFactory = useCallback(() => {
    const query = `query planFeature($featureName: String!) {
          planFeatureEligibility(featureName: $featureName) {
            eligible
            cause
          }
        }`;

    const resolver = ({ data }) => {
      return { eligibility: data.planFeatureEligibility };
    };

    return { query: query, resolver: createResolverFactory(resolver) };
  }, []);

  const feature = useMemo(() => {
    return { featureName };
  }, [featureName]);
  const showToaster = useMemo(() => {
    return showToasterOnError;
  }, [showToasterOnError]);

  const { loading, numOfFetches, data, error, ...props } = useQuery(
    queryFactory,
    {
      variables: feature,
      options: useMemo(() => {
        return options;
      }, [options]),
    }
  );

  const createErrorObj = useCallback(() => {
    return createError({
      ...parseGraphQLError(error),
      name: "GetPlanFeature",
      defaultMessage:
        `Sorry, errors encountered when processing your request for ${feature.featureName}. ` +
        "Please contact support for assistance.",
    });
  }, [error, feature]);

  useEffect(() => {
    if (error && !loading) {
      const err = createErrorObj();
      if (showToaster) fireError(err.message, err);
    }
  }, [fireError, error, loading, createErrorObj, showToaster]);

  return {
    loading: loading || (Boolean(options?.fetchOnInit) && numOfFetches === 0),
    error: error && !loading ? createErrorObj().message : null,
    ...data,
    ...props,
  };
}

export default usePlanFeature;
